import type { WeeklySlidesWeek } from "@/generated/graphql";
import { type Locale, format, monthShort, useLocale, useT } from "@/i18n";
import { useEffect, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

interface WeeklySlidesCardProps {
  totalSlides: number;
  weeks: WeeklySlidesWeek[];
  onPreviousPeriod: () => void;
  onNextPeriod: () => void;
  canGoToNextPeriod: boolean;
}

const PLOT_HEIGHT = 120;
/** A zero-count week still draws a stub, so it reads as "checked, had none", not a gap. */
const STUB_BAR_HEIGHT = 4;
/** The tallest bar stops here, leaving headroom for the selected week's callout. */
const TARGET_MAX_BAR_FRACTION = 0.75;

function parseWeekStart(date: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** "Sep 6–12", or "Aug 30–Sep 5" across a month boundary. Weeks are always a 7-day span. */
function formatWeekRange(locale: Locale, weekStart: string): string {
  const start = parseWeekStart(weekStart);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const startMonth = monthShort(locale, start.getMonth());
  const endMonth = monthShort(locale, end.getMonth());
  return startMonth === endMonth
    ? `${startMonth} ${start.getDate()}–${end.getDate()}`
    : `${startMonth} ${start.getDate()}–${endMonth} ${end.getDate()}`;
}

/** Consecutive weeks sharing a month, in bar order — one centered label per run of bars. */
function getMonthGroups(
  locale: Locale,
  weeks: WeeklySlidesWeek[]
): { month: string; weekCount: number }[] {
  const groups: { month: string; weekCount: number }[] = [];
  for (const week of weeks) {
    const month = monthShort(locale, parseWeekStart(week.weekStart).getMonth());
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.month === month) lastGroup.weekCount += 1;
    else groups.push({ month, weekCount: 1 });
  }
  return groups;
}

/** Three-level axis (0 / midpoint / top), top even so the midpoint stays a whole number. */
function getYAxisScale(maxCount: number): { top: number; midpoint: number } {
  const top = Math.max(Math.ceil(maxCount / TARGET_MAX_BAR_FRACTION / 2) * 2, 2);
  return { top, midpoint: top / 2 };
}

/** Slides practiced per week over a six-week window, one window at a time. */
export function WeeklySlidesCard({
  totalSlides,
  weeks,
  onPreviousPeriod,
  onNextPeriod,
  canGoToNextPeriod,
}: WeeklySlidesCardProps) {
  const t = useT();
  const locale = useLocale();
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null);

  // A week is always selected. Paging in brings a different set of weekStarts, so a selection
  // that no longer names a visible bar falls back to this period's most recent week.
  useEffect(() => {
    setSelectedWeekStart((current) => {
      if (current != null && weeks.some((week) => week.weekStart === current)) return current;
      return weeks[weeks.length - 1]?.weekStart ?? null;
    });
  }, [weeks]);

  const hasAnySlides = weeks.some((week) => week.count > 0);
  const { top, midpoint } = getYAxisScale(Math.max(...weeks.map((week) => week.count), 0));
  const selectedWeek =
    weeks.find((week) => week.weekStart === selectedWeekStart) ?? weeks[weeks.length - 1] ?? null;

  return (
    <div className="app-card px-5 py-4">
      <p className="app-label">
        {hasAnySlides ? t["report.weekly.title"] : t["report.weekly.empty"]}
      </p>

      <div className="mt-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onPreviousPeriod}
          aria-label={t["report.weekly.previousPeriod"]}
          className="flex h-8 w-8 items-center justify-center rounded-full text-app-muted hover:bg-gray-100"
        >
          <HiChevronLeft className="h-5 w-5" />
        </button>
        {selectedWeek && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-bold text-app-ink">
            {formatWeekRange(locale, selectedWeek.weekStart)}
          </span>
        )}
        <button
          type="button"
          onClick={onNextPeriod}
          disabled={!canGoToNextPeriod}
          aria-label={t["report.weekly.nextPeriod"]}
          className="flex h-8 w-8 items-center justify-center rounded-full text-app-muted hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <HiChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-6 flex gap-2">
        <div className="min-w-0 flex-1">
          <div className="relative" style={{ height: PLOT_HEIGHT }}>
            {[0, PLOT_HEIGHT / 2, PLOT_HEIGHT].map((offset) => (
              <div
                key={offset}
                className="absolute inset-x-0 border-t border-gray-200"
                style={{ bottom: offset }}
              />
            ))}
            <div className="absolute inset-0 flex items-end gap-2">
              {weeks.map((week) => {
                const isSelected = week.weekStart === selectedWeek?.weekStart;
                const height =
                  week.count > 0 ? Math.round((week.count / top) * PLOT_HEIGHT) : STUB_BAR_HEIGHT;
                return (
                  <button
                    type="button"
                    key={week.weekStart}
                    onClick={() => setSelectedWeekStart(week.weekStart)}
                    aria-label={format(t["report.weekly.barA11y"], {
                      range: formatWeekRange(locale, week.weekStart),
                      count: week.count,
                    })}
                    className="relative flex flex-1 items-end justify-center"
                    style={{ height: PLOT_HEIGHT }}
                  >
                    {isSelected && (
                      <span
                        className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center leading-tight"
                        style={{ bottom: height + 4 }}
                      >
                        <span className="block font-kumbh text-base font-extrabold text-app-ink">
                          {week.count}
                        </span>
                        <span className="block text-[10px] font-semibold text-app-muted">
                          {week.count === 1
                            ? t["report.weekly.slideOne"]
                            : t["report.weekly.slideOther"]}
                        </span>
                      </span>
                    )}
                    <span
                      className={`w-full max-w-[28px] rounded-t-sm ${isSelected ? "bg-app-blue" : "bg-[#DFDFDF]"}`}
                      style={{ height }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-1.5 flex gap-2">
            {getMonthGroups(locale, weeks).map((group) => (
              <span
                key={group.month}
                className="text-center text-[10px] font-semibold text-app-muted"
                style={{ flex: group.weekCount }}
              >
                {group.month}
              </span>
            ))}
          </div>
        </div>

        <div
          className="flex flex-col justify-between text-right text-[10px] font-semibold tabular-nums text-app-muted"
          style={{ height: PLOT_HEIGHT }}
        >
          <span className="-translate-y-1/2">{top}</span>
          <span>{midpoint}</span>
          <span className="translate-y-1/2">0</span>
        </div>
      </div>

      <p className="body-sm mt-4 text-app-text">
        {t["report.weekly.totalPrefix"]}
        <span className="font-kumbh font-extrabold tabular-nums">{totalSlides}</span>
        {t["report.weekly.totalSuffix"]}
      </p>
    </div>
  );
}
